import { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../../components/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { toast } from 'sonner';
import { AlertTriangle, Building2, Clock, Cog, CheckCircle, Image } from 'lucide-react';
import { LazyPhotoGallery } from '../../components/PhotoComponents';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TechIncidents() {
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchIncidents();
  }, []);

  const fetchIncidents = async () => {
    try {
      const response = await axios.get(`${API}/incidents`);
      setIncidents(response.data);
    } catch (error) {
      toast.error('Error al cargar incidencias');
    } finally {
      setLoading(false);
    }
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      baja: 'text-slate-400',
      media: 'text-amber-400',
      alta: 'text-orange-400',
      urgente: 'text-rose-400',
    };
    return <span className={`font-mono text-xs uppercase ${styles[priority]}`}>{priority}</span>;
  };

  const getStatusBadge = (status) => {
    const styles = {
      pendiente: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
      en_proceso: 'bg-sky-500/10 text-sky-400 border-sky-500/20',
      resuelto: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    };
    const labels = { pendiente: 'Pendiente', en_proceso: 'En Proceso', resuelto: 'Resuelto' };
    return (
      <span className={`px-2.5 py-1 rounded-full text-xs font-mono uppercase tracking-wider border ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  const getStatusIcon = (status) => {
    const icons = {
      pendiente: <AlertTriangle className="w-5 h-5 text-amber-400" />,
      en_proceso: <Clock className="w-5 h-5 text-sky-400" />,
      resuelto: <CheckCircle className="w-5 h-5 text-emerald-400" />,
    };
    return icons[status];
  };

  // Group by status
  const pendingIncidents = incidents.filter(i => i.status === 'pendiente');
  const inProgressIncidents = incidents.filter(i => i.status === 'en_proceso');
  const resolvedIncidents = incidents.filter(i => i.status === 'resuelto').slice(0, 5);

  return (
    <Layout title="Incidencias Reportadas">
      <div className="space-y-6 animate-fade-in" data-testid="tech-incidents">
        <p className="text-slate-400">Consulta las incidencias reportadas por los clientes</p>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <Card className="bg-amber-500/5 border-amber-500/20">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-rajdhani font-bold text-amber-400">{pendingIncidents.length}</p>
                <p className="text-xs text-amber-400/70 uppercase tracking-wider">Pendientes</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-sky-500/5 border-sky-500/20">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-sky-500/20 flex items-center justify-center">
                <Clock className="w-5 h-5 text-sky-400" />
              </div>
              <div>
                <p className="text-2xl font-rajdhani font-bold text-sky-400">{inProgressIncidents.length}</p>
                <p className="text-xs text-sky-400/70 uppercase tracking-wider">En Proceso</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-emerald-500/5 border-emerald-500/20">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-rajdhani font-bold text-emerald-400">{resolvedIncidents.length}</p>
                <p className="text-xs text-emerald-400/70 uppercase tracking-wider">Resueltas</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Incidents List */}
        {loading ? (
          <div className="text-center py-12 text-slate-500">Cargando...</div>
        ) : incidents.length === 0 ? (
          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="py-12 text-center">
              <AlertTriangle className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No hay incidencias registradas</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {/* Pending first, then in progress, then resolved */}
            {[...pendingIncidents, ...inProgressIncidents, ...resolvedIncidents].map((incident) => (
              <Card 
                key={incident.id} 
                className={`bg-slate-900/50 border-slate-800 hover:border-slate-700 transition-all ${
                  incident.priority === 'urgente' ? 'border-l-2 border-l-rose-500' : 
                  incident.priority === 'alta' ? 'border-l-2 border-l-orange-500' : ''
                }`}
                data-testid={`incident-${incident.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center flex-shrink-0">
                      {getStatusIcon(incident.status)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        {getStatusBadge(incident.status)}
                        {getPriorityBadge(incident.priority)}
                        <span className="text-sm text-slate-400 flex items-center gap-1">
                          <Building2 className="w-4 h-4" />
                          {incident.company_name}
                        </span>
                      </div>
                      <h3 className="text-lg font-semibold text-white mb-1">{incident.title}</h3>
                      <p className="text-slate-400 mb-3">{incident.description}</p>
                      {/* Photos */}
                      {incident.photo_count > 0 && (
                        <div className="mb-3">
                          <LazyPhotoGallery id={incident.id} type="incidents" count={incident.photo_count} />
                        </div>
                      )}
                      <div className="flex items-center gap-4 text-sm text-slate-500">
                        {incident.machine_name && (
                          <span className="flex items-center gap-1">
                            <Cog className="w-4 h-4" />
                            {incident.machine_name}
                          </span>
                        )}
                        <span className="flex items-center gap-1 font-mono">
                          <Clock className="w-4 h-4" />
                          {new Date(incident.created_at).toLocaleDateString('es-ES')}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
